import { z } from 'zod';

/**
 * Currencies the UI can present prices in. Plans are *billed* in their own
 * currency (USD today); these are display-only conversions backed by the
 * fx_rates table. Lowercase ISO 4217 to match Stripe's convention.
 */
export const SUPPORTED_DISPLAY_CURRENCIES = [
  'usd',
  'gbp',
  'aud',
  'nzd',
  'cad',
  'eur',
] as const;
export const DisplayCurrencySchema = z.enum(SUPPORTED_DISPLAY_CURRENCIES);
export type DisplayCurrency = z.infer<typeof DisplayCurrencySchema>;

/**
 * Approximate price in the viewer's display currency. Computed server-side
 * from the plan's base price and the latest FX rate. Null when no rate is
 * available — the UI then shows only the base price.
 */
export const DisplayPriceSchema = z.object({
  currency: z.string().length(3),
  amountCents: z.number().int().nonnegative(),
  fxRate: z.number().positive(),
  asOf: z.string().datetime(),
});
export type DisplayPrice = z.infer<typeof DisplayPriceSchema>;

/**
 * Plan DTO returned by `GET /v1/plans`. Both the marketing pricing page and
 * the dashboard upgrade flow render this exact shape.
 */
export const PlanSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  callsIncluded: z.number().int().nonnegative(),
  /** Inbound voice minutes included per billing period. */
  voiceMinutesIncluded: z.number().int().nonnegative(),
  /** Chat messages included per billing period. */
  chatMessagesIncluded: z.number().int().nonnegative(),
  /** Active phone numbers an org on this plan may hold at once. 0 = unlimited. */
  phoneNumbersIncluded: z.number().int().nonnegative(),
  isTrial: z.boolean(),
  trialDays: z.number().int().nullable(),
  intervalDays: z.number().int().positive(),
  sortOrder: z.number().int(),
  /** Daily chat AI-spend ceiling in cents (abuse cap, not an invoice).
   *  0 = unlimited. Surfaced on the pricing page as a per-tier row. */
  chatDailySpendCapCents: z.number().int().nonnegative(),
  /** Whether caller-transfer / after-hours forwarding is included in this tier.
   *  Higher-tier upsell; rendered as a per-plan comparison row. */
  callTransferEnabled: z.boolean(),
  /** Opt-in overage rates. Voice: cents per extra minute. Chat: cents per
   *  whole block of 1,000 extra messages (ceil). Null = overage not offered
   *  on this plan (trial). Phase C. */
  overagePerMinuteCents: z.number().int().nonnegative().nullable(),
  overagePer1000MessagesCents: z.number().int().nonnegative().nullable(),
  /** Present when the request asked for a `displayCurrency` that differs
   *  from the plan's billing currency and an FX rate exists. */
  displayPrice: DisplayPriceSchema.nullable().optional(),
});

export type Plan = z.infer<typeof PlanSchema>;

/**
 * Admin-only DTOs for plan CRUD. Used by `/v1/admin/plans/*`.
 */
export const PlanInputSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'slug must be lowercase letters, digits, and dashes'),
  name: z.string().min(1).max(100),
  description: z.string().nullable().optional(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  callsIncluded: z.number().int().nonnegative(),
  voiceMinutesIncluded: z.number().int().nonnegative(),
  chatMessagesIncluded: z.number().int().nonnegative(),
  /** Phone-number allowance for orgs on this plan. 0 = unlimited. Defaults to
   *  1 when omitted (matches the DB default). */
  phoneNumbersIncluded: z.number().int().nonnegative().optional(),
  isTrial: z.boolean(),
  trialDays: z.number().int().positive().nullable().optional(),
  intervalDays: z.number().int().positive(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  /** False = custom deal plan: subscribable but hidden from the public
   *  pricing list. Defaults to true when omitted. */
  isPublic: z.boolean().optional(),
  callTransferEnabled: z.boolean(),
  /** Whether the embeddable chat widget is included. Defaults to true. */
  chatEnabled: z.boolean().optional(),
  /** Per-org daily chat AI-spend ceiling in cents. 0 = unlimited. */
  chatDailySpendCapCents: z.number().int().nonnegative().optional(),
  /** Opt-in overage rates; null = overage not offered on this plan. */
  overagePerMinuteCents: z.number().int().nonnegative().nullable().optional(),
  overagePer1000MessagesCents: z.number().int().nonnegative().nullable().optional(),
  stripeProductId: z.string().nullable().optional(),
  stripePriceId: z.string().nullable().optional(),
});
export type PlanInput = z.infer<typeof PlanInputSchema>;

export const PlanUpdateSchema = PlanInputSchema.partial();
export type PlanUpdate = z.infer<typeof PlanUpdateSchema>;
